import React from 'react';
import { Card, Badge } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { Package, FlaskConical, CalendarDays, ShieldCheck, LineChart, ArrowRight, CircleDollarSign } from 'lucide-react';
import { cn } from '@/utils/cn';

export function AgroModulesGrid() {
  const navigate = useNavigate();

  const MODULES = [
    {
      id: 'products',
      title: 'Vật tư Nông nghiệp',
      description: 'Quản lý danh mục phân bón, thuốc BVTV, vật tư cải tạo đất và theo dõi tồn kho.',
      icon: <Package className="w-6 h-6" />,
      color: 'blue',
      path: '/agro/products',
      stats: '156 Danh mục',
      gradient: 'from-blue-500 to-cyan-500',
      bgLight: 'bg-blue-50',
      textDark: 'text-blue-700'
    },
    {
      id: 'recipes',
      title: 'Công thức & Pha chế',
      description: 'Thiết lập tỷ lệ pha trộn an toàn, định mức sử dụng theo từng loại cây trồng.',
      icon: <FlaskConical className="w-6 h-6" />,
      color: 'purple',
      path: '/agro/recipes',
      stats: '42 Công thức chuẩn',
      gradient: 'from-purple-500 to-pink-500',
      bgLight: 'bg-purple-50',
      textDark: 'text-purple-700'
    },
    {
      id: 'applications',
      title: 'Lịch Phun & Bón',
      description: 'Lập kế hoạch, theo dõi tiến độ thi công và phân bổ vật tư thực tế cho dự án.',
      icon: <CalendarDays className="w-6 h-6" />,
      color: 'emerald',
      path: '/agro/applications',
      stats: '12 Đang diễn ra',
      gradient: 'from-emerald-400 to-teal-500',
      bgLight: 'bg-emerald-50',
      textDark: 'text-emerald-700'
    },
    {
      id: 'safety',
      title: 'An toàn & Tuân thủ',
      description: 'Ghi nhận trang bị bảo hộ (PPE), thời gian cách ly và nhật ký an toàn.',
      icon: <ShieldCheck className="w-6 h-6" />,
      color: 'amber',
      path: '/agro/safety',
      stats: '100% Tuân thủ tuần',
      gradient: 'from-amber-400 to-orange-500',
      bgLight: 'bg-amber-50',
      textDark: 'text-amber-700'
    },
    {
      id: 'effectiveness',
      title: 'Đánh giá Hiệu quả',
      description: 'Phân tích kết quả sinh trưởng sau khi xử lý phân thuốc, đo lường ROI.',
      icon: <LineChart className="w-6 h-6" />,
      color: 'indigo',
      path: '/agro/effectiveness',
      stats: '95.2% Đạt chuẩn',
      gradient: 'from-indigo-500 to-violet-500',
      bgLight: 'bg-indigo-50',
      textDark: 'text-indigo-700'
    },
    {
      id: 'pricing',
      title: 'Đơn giá & Dự toán',
      description: 'Quản lý đơn giá dự toán, theo dõi và đối chiếu với đơn giá nhập thực tế của vật tư nông nghiệp.',
      icon: <CircleDollarSign className="w-6 h-6" />,
      color: 'rose',
      path: '/agro/rates',
      stats: 'Tự động cập nhật',
      gradient: 'from-rose-500 to-red-500',
      bgLight: 'bg-rose-50',
      textDark: 'text-rose-700'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
      {MODULES.map((mod) => (
        <Card
          key={mod.id}
          onClick={() => navigate(mod.path)}
          className="group relative overflow-hidden bg-white border-2 border-slate-100 hover:border-emerald-200 shadow-sm hover:shadow-xl rounded-3xl p-6 cursor-pointer transition-all duration-300 hover:-translate-y-1 h-full flex flex-col"
        >
          <div className={cn("absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-5 rounded-bl-full transition-transform duration-500 group-hover:scale-110", mod.gradient)} />

          <div className="flex items-start justify-between mb-4">
            <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3", mod.bgLight, mod.textDark)}>
              {mod.icon}
            </div>
            <Badge variant="custom" className={cn("bg-slate-50 text-slate-600 border border-slate-100 font-bold px-2.5 py-1 text-[10px]")}>
              {mod.stats}
            </Badge>
          </div>

          <h4 className="text-lg font-black text-slate-800 mb-2 group-hover:text-emerald-700 transition-colors">{mod.title}</h4>
          <p className="text-xs text-slate-500 leading-relaxed mb-6 line-clamp-2 min-h-[36px] flex-grow">
            {mod.description}
          </p>

          <div className="flex items-center text-sm font-bold text-emerald-600 opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 mt-auto">
            Truy cập ngay <ArrowRight className="w-4 h-4 ml-1.5" />
          </div>
        </Card>
      ))}
    </div>
  );
}
